import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { throwError } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import { SimpleUploadComponent } from '../../simple-upload/simple-upload.component';
import { UploadAttachmentsEndpoints } from '../entities/upload-attachments-endpoints';

@Injectable()
export class SimpleUploadService {
  private maxFileSizePurchaseOrder = 10485760;

  constructor(
    private apiService: ApiService,
    private matDialogService: MatDialog,
    private exportUtilsService: ExportUtilsService,
    private deviceService: DeviceDetectorService,
    private appInfoService: AppInfoService) {
  }

  /**
   * Opens a confirmation box. To be called from a component or service.
   * @param endpoints - The list of endpoints for list, upload, download, delete
   * @param entityId - The container entity Id used for upload and list operations
   * @param uploadName - The upload entity name
   */
  openUploadDialog(endpoints: UploadAttachmentsEndpoints, entityId: string, uploadName: string, isReadOnly?) {
    const dialogParams = {
      closeOnNavigation: true,
      autoFocus: true,
      data: {
        fileChangeEvent: (event) => this.uploadFile(event, endpoints.uploadUrl, uploadName, entityId),
        listFiles: () => this.listFiles(endpoints.listUrl + entityId),
        downloadFile: (fileId: string, fileName: string) => this.downloadFile(endpoints.downloadUrl + fileId, fileName),
        deleteFile: (fileId: string) => this.deleteFiles(endpoints.deleteUrl, fileId),
        isReadOnly: isReadOnly ? true : null
      },
      panelClass: this.deviceService.isMobile() ? ['ds', 'fullWidth', 'fullScreen'] : ['ds', 'fullWidth']
    };

    const dialogRef = this.matDialogService.open(SimpleUploadComponent, dialogParams);

    return dialogRef.beforeClosed().toPromise();
  }

  /**
   * Uploads the file upload
   * @param event - The upload event
   * @param uploadUrl - The upload URL
   * @param uploadName - The upload name for the file entity
   * @param entityId - The container entity Id used for upload and list operations
   */
  private uploadFile(event: any, uploadUrl: string, uploadName: string, entityId: string) {
    const fileList: FileList = event;

    if (fileList.length > 0) {
      const file: File = fileList[0];
      const formData: FormData = new FormData();
      const fileSize = file.size;
      if (uploadName === 'PurchaseOrderAttachment') {
        if (fileSize > this.maxFileSizePurchaseOrder) {
          return throwError(this.appInfoService.appStrings.paymentDetails_purchaseOrderSizeError);
        }
      }

      formData.append(uploadName, file, file.name);
      formData.append('id', entityId);

      return this.apiService.post(uploadUrl, formData);
    }
  }

  /**
   * Download the requested file
   * @param getUrl - The url (with entity)
   * @param fileName - The file name
   */
  private downloadFile(getUrl: string, fileName: string) {
    return this.apiService.getBlob(getUrl)
      .subscribe((result: ArrayBuffer) => this.exportUtilsService.downloadFile(result, fileName));
  }

  /**
   * Performs a simple call using the provided url
   * @param getUrl - The url (with entity)
   */
  listFiles(getUrl: string) {
    return this.apiService.get(getUrl);
  }

  /**
   * Performs a simple call using the provided url
   * @param deleteUrl - The delete url
   * @param attachmentId - The attachment id
   */
  private deleteFiles(deleteUrl: string, attachmentId: string) {
    return this.apiService.post(deleteUrl, { id: attachmentId });
  }

}
